import { Injectable } from '@angular/core';

/**
 * Service to map insurance type names to Material Icons
 * Maps insurance type display names (e.g., "Health", "Travel") to icon names
 * Only specific types are mapped, all others default to 'description'
 */
@Injectable({
  providedIn: 'root',
})
export class InsuranceTypeIconService {
  /**
   * Map of insurance type names to Material Icon names
   * Only contains specific mappings - all other types default to 'description'
   * Uses lowercase display names as keys for case-insensitive matching
   */
  private readonly iconMap: Record<string, string> = {
    health: 'health_and_safety',
    life: 'favorite',
    student: 'school',
    telemedicine: 'medical_services',
    travel: 'flight',
  };

  /**
   * Default icon name for unmapped insurance types
   */
  private readonly defaultIcon = 'description';

  /**
   * Get Material Icon name for insurance type
   * @param insuranceTypeName - Insurance type display name (e.g., "Health", "Travel", "Life", "Student")
   * @returns Material Icon name, defaults to 'description' if not found in iconMap
   */
  getIcon(insuranceTypeName: string | null | undefined): string {
    if (!insuranceTypeName) {
      return this.defaultIcon;
    }

    // Convert to lowercase for case-insensitive comparison
    const normalizedName = insuranceTypeName.toLowerCase().trim();

    // Return mapped icon or default
    return this.iconMap[normalizedName] || this.defaultIcon;
  }

  /**
   * Get all available insurance type icon mappings
   * @returns Record of insurance type names to icon names (only mapped types)
   */
  getAllIcons(): Record<string, string> {
    return { ...this.iconMap };
  }
}
